import { useState, useCallback } from "react";
import type { CalendarEvent } from "@tellescope/types-models";
import { useMeetingOperations } from "@tellescope/video-calling";
import { useVideoCalling } from "../store";

interface UseJoinVideoCallOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseJoinVideoCallReturn {
  joinVideoCall: (event: CalendarEvent) => Promise<void>;
  isJoining: boolean;
  error: Error | null;
}

/**
 * Hook for joining video calls from calendar events.
 * Handles setting up meeting metadata, opening the video dialog, and joining the meeting.
 *
 * @example
 * const { joinVideoCall, isJoining } = useJoinVideoCall();
 *
 * const handleJoin = async () => {
 *   if (!event) return;
 *   await joinVideoCall(event);
 * };
 */
export const useJoinVideoCall = (options: UseJoinVideoCallOptions = {}): UseJoinVideoCallReturn => {
  const videoCalling = useVideoCalling();
  const { joinMeeting, isJoining } = useMeetingOperations();
  const [error, setError] = useState<Error | null>(null);

  const joinVideoCall = useCallback(
    async (event: CalendarEvent) => {
      // Reset errors
      setError(null);
      videoCalling.setConnectionError(null);

      // Set meeting metadata
      videoCalling.setMeetingMeta({
        calendarEventId: event.id,
        eventTitle: event.title,
        isHost: false,
      });

      // Configure UI state
      videoCalling.setIsWaitingRoom(false);
      videoCalling.setIsVisible(true);

      try {
        await joinMeeting(event.id);
        options.onSuccess?.();
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to join meeting");
        setError(error);
        videoCalling.setConnectionError(error.message);
        options.onError?.(error);
      }
    },
    [videoCalling, joinMeeting, options]
  );

  return {
    joinVideoCall,
    isJoining,
    error,
  };
};
